import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Link,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { JUDGE_POSITIONS, sectionJudgeIds, showSectionStyle, sortJudgesForDisplay, sponsorPrizeModeOf } from "score-common";
import { useTranslation } from "i18n-fe";
import { useActiveOrg, useActiveShow, TableSkeleton, userDisplayName } from "common-fe";
// #1805 — formatStartTime's `v` is scheduledTime, an FVD Access stamp
// (#1811), not a genuine instant, so it renders through the shared
// FVD_STAMP_TIMEZONE constant, not a local "America/Los_Angeles" literal.
import { stampZoneOf } from "common-fe/venueTime";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useReportApi, unwrapReport } from "../api/reportApi";
import { useCanAssignScribes } from "../hooks/useCanAssignScribes";
import {
  classesWithPlacingsAtRisk,
  classesWithPlacingsAtRiskForPlaces,
  isPublishedFederationStatus,
} from "../util/placedByDivision";
import PlacedByDivisionConfirmDialog from "../components/PlacedByDivisionConfirmDialog";
import { classes as classesCache } from "../local/db";
import { stripRideDerivedFields } from "../util/stripRideDerivedFields";
import { useOrgMembers } from "../hooks/useOrgMembers";
import { classNumberLabel, sortByClassNumber, classDay, classDayKey, formatDayKey } from "../util/classDisplay";
import { CLASS_PROBLEMS, classProblem, normalizeRing } from "../util/ringDayTable";
import ClassDetailDrawer from "../components/ClassDetailDrawer";
import NeedsPlacingsChip from "../components/NeedsPlacingsChip";
import CantPlaceYetChip from "../components/CantPlaceYetChip";
import PlaceProblemChip from "../components/PlaceProblemChip";
import usePlaceProblems from "../hooks/usePlaceProblems";
import ClassLink from "../components/ClassLink";
import { classesWithRidersAndNoScribe } from "../util/noScribeWithRiders";
import AddFilterMenu from "../components/AddFilterMenu";
import FvdPushStatusIcon from "../components/FvdPushStatusIcon";
import BulkAssignScribesDialog, {
  DSHB_KIND_OVERRIDE_AUTO,
} from "../components/BulkAssignScribesDialog";
import { useClassesNeedingPlacings } from "../hooks/useClassesNeedingPlacings";
import { useBadgeCounts } from "../context/BadgeCountsContext";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { distinctRealRideTestIds } from "../util/rideTestSlugs";
import {
  DEFAULT_CLASS_LIST_GROUP_BY,
  DEFAULT_CLASS_LIST_SORT_BY,
  getStoredClassListGroupBy,
  getStoredClassListSortBy,
  storeClassListGroupBy,
  storeClassListSortBy,
} from "../util/classListPref";
import TestDisagreementChip from "../components/TestDisagreementChip";

// #413 wire form: the grantable scribe role qualified for the token.
const SCRIBE_ROLE = "score:score_scribe";

// #1783/#2421 — same equitation-only gate ClassDetailDrawer's walk-trot
// Switch uses (prod check: 19/19 dressage-seat equitation classes carry
// sectionCode "6900", 0/380 non-equitation classes do), needed here for the
// bulk write loop's per-class applicability check.
const EQUITATION_SECTION_CODE = "6900";

// The judge-booth positions worth showing a scribe select for on a class: ONLY
// the ones the class actually staffs (from FVD's judge panel, #555).
function positionsForClass(classItem) {
  return JUDGE_POSITIONS.filter(
    (p) => Array.isArray(classItem.judgeAssignments?.[p]) && classItem.judgeAssignments[p].length,
  );
}

// #573 — a class's scheduled start time for the list column. scheduledTime is
// an FVD-stamped string; render an ISO datetime as a short local time, and
// leave an already-formatted string ("8:30 AM") untouched.
// #1814 AC3 -- `scheduledTimeZone` is the row's optional marker (see stampZoneOf).
function formatStartTime(v, scheduledTimeZone) {
  if (!v) return null;
  const d = new Date(v);
  if (!Number.isNaN(d.getTime()) && /\d{4}-\d{2}-\d{2}/.test(v)) {
    // #612 / #1805 — render the FVD stamp through FVD_STAMP_TIMEZONE.
    return d.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      timeZone: stampZoneOf(scheduledTimeZone),
    });
  }
  return String(v);
}

// A one-line summary of a class's scribe assignments for the list row, e.g.
// "C: Jane · H: Bob". Empty string when nothing is assigned.
function scribeSummary(classItem, scribeLabel) {
  return positionsForClass(classItem)
    .map((p) => {
      const ids = classItem.scribeAssignments?.[p];
      const id = Array.isArray(ids) && ids.length ? ids[0] : null;
      return id ? `${p}: ${scribeLabel(id)}` : null;
    })
    .filter(Boolean)
    .join(" · ");
}

// #1062 — a one-line summary of a class's FVD-reconciled judge panel for the
// list row, e.g. "C: Jane Smith · H: Bob Jones". Reads judgeAssignments
// directly (plain FVD name strings, not roster ids — no lookup needed).
// Empty string when the class has no judge panel yet.
function judgeSummary(classItem) {
  // #2788: Object.entries follows insertion order (whatever FVD reconcile
  // wrote first), so sort to the arena order (E, H, C, M, B, #2786).
  return sortJudgesForDisplay(
    Object.entries(classItem.judgeAssignments || {}),
    ([position]) => position,
  )
    .filter(([, names]) => Array.isArray(names) && names.length)
    .map(([position, names]) => `${position}: ${names.filter(Boolean).join(", ")}`)
    .join(" · ");
}

// #656 — the judged positions that still have no scribe, e.g. ["B"]. Empty
// array when every judged position is covered (or there are none to cover).
function missingScribePositions(classItem) {
  return positionsForClass(classItem).filter(
    (p) => !(Array.isArray(classItem.scribeAssignments?.[p]) && classItem.scribeAssignments[p].length),
  );
}

// #593 — a class is "missing a scribe" only when it HAS judged positions (so it
// needs scribing) and at least one of them has no scribe assigned. Classes with
// no judge panel can't be scribed, so they are NOT flagged — otherwise the
// filter/label matches nearly every class and looks like it does nothing.
function isMissingScribe(classItem) {
  const positions = positionsForClass(classItem);
  return (
    positions.length > 0 &&
    positions.some(
      (p) =>
        !(Array.isArray(classItem.scribeAssignments?.[p]) && classItem.scribeAssignments[p].length),
    )
  );
}

// score-api's POST /class/bulk-config cap (MAX_BATCH_IDS).
const BULK_CLASS_CONFIG_MAX_CLASSES = 200;

// #3240 — filters offered in the Add-filter dropdown. `key` is what
// `appliedFilters` holds; the filter rules themselves live in `visible`.
const CLASS_FILTERS = [
  { key: "noScribe", labelKey: "ring.filterNoScribe" },
  // #3681 -- the badge's set: ringed, has rides, no scribe at ANY position
  // (classesWithRidersAndNoScribe). NOT the same set as noScribe above.
  { key: "noScribeWithRiders", labelKey: "ring.filterNoScribeWithRiders" },
  { key: "needsPlacings", labelKey: "ring.filterNeedsPlacings" },
  // #3651 -- every class that cannot be placed, for ANY reason (the server's
  // stored Placing problems, GET /show/:showId/place-problems).
  { key: "cantPlace", labelKey: "ring.filterPlacingProblems" },
];

export default function ClassManager() {
  const { t } = useTranslation("scoreadmin");
  const navigate = useNavigate();
  // #863 — the URL is the source of truth for which class's detail drawer is
  // open (mirrors /rides/:rideId, App.jsx). No local selectedClassId state:
  // selecting a row or closing the drawer navigates, and the drawer's own
  // open state just falls out of whether classId resolves to a known class
  // (see selectedClass below) — an unknown/stale id degrades to "closed"
  // rather than throwing.
  const { classId: selectedClassId = null } = useParams();
  // #3652 -- Settings "Rings and days" links here as ?ring=<ring>&day=<YYYY-MM-DD>.
  // The ring compares whitespace-collapsed (normalizeRing) and the day through
  // classDayKey, the same two definitions the Settings table counts with.
  const [searchParams, setSearchParams] = useSearchParams();
  const ringParam = normalizeRing(searchParams.get("ring"));
  const dayParam = searchParams.get("day") || "";
  // #3662 -- ?problem=noRing|noDay lifts the #591 hide for exactly the classes
  // the Settings "Rings and days" problem lines count (an unknown value is ignored).
  const problemParam = CLASS_PROBLEMS.includes(searchParams.get("problem")) ? searchParams.get("problem") : "";
  const clearUrlFilter = useCallback(
    (name) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(name);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const { activeOrgId } = useActiveOrg();
  const { activeShowId, activeShow, loading: loadingShow } = useActiveShow();
  const scoreApi = useScoreApi();
  const reportApi = useReportApi();
  const canAssignScribes = useCanAssignScribes();
  // #2339 — paged org-members fetch (follows nextCursor to the end), shared
  // with PeopleManager's Scribes tab and useOrgMembersById.
  // `loading` feeds only the "no scribes" alert (#3187), never the page
  // skeleton (see the #2700 note below).
  const { members: orgMembers, loading: membersLoading, error: scribesError } = useOrgMembers(activeOrgId);

  const [classes, setClasses] = useState([]);
  // #2700 — flips true once the FIRST fresh classes fetch for this mount has
  // settled (success or failure). Gates the empty state ("no classes found")
  // and (together with `classes.length`) the full-page skeleton — see the
  // skeleton branch and the ring.noClasses branch below.
  const [classesReady, setClassesReady] = useState(false);
  const [judges, setJudges] = useState([]);
  const [testsById, setTestsById] = useState({});
  const [sponsors, setSponsors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // #553 — list controls + the selected class for the detail drawer.
  const [search, setSearch] = useState("");
  // #2782 — groupBy/sortBy are seeded from the operator's last manual pick
  // (util/classListPref.js's localStorage slots, bugs board #256) and every
  // manual pick is written back through the change handlers below, so both
  // survive a reload and a navigate-away-and-back. A first-ever visit with
  // nothing stored falls through to the pre-#2782 defaults (ring grouping,
  // number sort).
  const [groupBy, setGroupBy] = useState(
    () => getStoredClassListGroupBy() ?? DEFAULT_CLASS_LIST_GROUP_BY,
  ); // none | day | ring | day-ring
  const [sortBy, setSortBy] = useState(
    () => getStoredClassListSortBy() ?? DEFAULT_CLASS_LIST_SORT_BY,
  ); // number | name | time
  const changeGroupBy = useCallback((value) => {
    setGroupBy(value);
    storeClassListGroupBy(value);
  }, []);
  const changeSortBy = useCallback((value) => {
    setSortBy(value);
    storeClassListSortBy(value);
  }, []);
  // #575 — show only classes with no scribe assigned to any judged position.
  const [appliedFilters, setAppliedFilters] = useState([]);
  const noScribeOnly = appliedFilters.includes("noScribe");
  // #340 — show only classes that are scored but have no placings recorded.
  const needsPlacingsOnly = appliedFilters.includes("needsPlacings");
  // #3651 -- only classes the server holds a Placing problem for.
  const cantPlaceOnly = appliedFilters.includes("cantPlace");
  // #3681 -- only classes with riders and no scribe (the header badge's set).
  const noScribeWithRidersOnly = appliedFilters.includes("noScribeWithRiders");
  // #574 — per-group collapse state; a key present here is collapsed.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  // #1055 — Gmail-style bulk selection: a Set of selected class ids. Kept
  // independent of the filter/group state above — selecting some rows and
  // then narrowing the filter doesn't silently drop the selection, it just
  // takes those rows out of view (Gmail's own behavior). "Select all" only
  // ever touches the currently-VISIBLE (filtered) rows, never the full set.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  // #2421 — a bulk submit that half-succeeds is reported, never silently
  // treated as a clean success (card's "Partial failure" section). Cleared
  // whenever the dialog (re)opens; kept (and the dialog kept open) across a
  // failed submit so the operator sees exactly which classes/fields failed.
  const [bulkFailures, setBulkFailures] = useState([]);
  // #2421 repair — how many writes the last bulk submit actually attempted,
  // so the partial-failure banner can tell "some failed" from "every write
  // failed" (below) instead of always claiming the rest still applied.
  const [bulkWritesTotal, setBulkWritesTotal] = useState(0);
  // #2519: a class-field pair the topology gate or the fault-time-limit
  // bulk-clear guard (both below) deliberately did not write, because
  // writing it would 400 or be silently discarded server-side. These are
  // NOT failures (no write was ever attempted) but must be surfaced with
  // the same "never a clean success over a dropped write" discipline as
  // bulkFailures: an operator who believes a skipped field was applied is
  // the harm this card exists to close. Cleared whenever the dialog
  // (re)opens; kept (and the dialog kept open) across a submit that skipped
  // anything so the operator sees exactly which classes/fields were skipped
  // and why.
  const [bulkSkips, setBulkSkips] = useState([]);
  // #3405 (placed-by-division): what the server did to each class's placings
  // after the last save that touched Placed by division. `bulkOutcomes` is the
  // bulk dialog's per-class list; `placedByDivisionOutcomes` is the drawer's
  // (classId -> replaced | cleared | none) and `placedByDivisionConfirm` is the
  // pending single-class change awaiting the re-place confirm.
  const [bulkOutcomes, setBulkOutcomes] = useState(null);
  const [placedByDivisionOutcomes, setPlacedByDivisionOutcomes] = useState({});
  const [placedByDivisionConfirm, setPlacedByDivisionConfirm] = useState(null);
  // #3415: the same two for Places awarded (a real change re-places the class too).
  const [placesAwardedOutcomes, setPlacesAwardedOutcomes] = useState({});
  const [placesAwardedConfirm, setPlacesAwardedConfirm] = useState(null);

  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Derive the scribe-roled members for the active org from the shared,
  // fully-paged useOrgMembers hook above. #2700 judge round 1 finding 5 —
  // scribesLoading no longer gates the classes-table skeleton (it has
  // nothing to do with which classes render, only the scribe column inside
  // each row), so there is no `loading` state to sync it into any more.
  useEffect(() => {
    if (scribesError) setError(scribesError);
  }, [scribesError]);

  // #3187 -- derived (not synced through an effect) so the scribe list and
  // `membersLoading` flip in the SAME commit: an effect-synced list lagged one
  // frame behind the loaded members and flashed "no scribes" on a full page.
  const scribes = useMemo(
    () => orgMembers.filter((m) => (m.roles || []).includes(SCRIBE_ROLE)),
    [orgMembers],
  );

  // (Re)load the selected show's classes. Also writes the fresh list into
  // the local classes cache (#2700, best-effort — never blocks/holds up the
  // render), stripped of ride-derived fields (stripRideDerivedFields above).
  const loadClasses = useCallback(
    async (id) => {
      if (!id) {
        setClasses([]);
        return;
      }
      const list = unwrap(await scoreApi.classes.listClasses(id));
      setClasses(list);
      classesCache.putAll(list.map(stripRideDerivedFields)).catch(() => {});
    },
    [scoreApi],
  );

  // #2700 — cold-mount sequence: paint whatever's cached for this show
  // immediately (before the fresh fetch below resolves), per Jaybill's
  // caching-constraint comment — classes and their order are safe to cache.
  // `classesReady` resets to false on every run of this effect (i.e. every
  // mount, and every activeShowId change) and only flips true once the
  // fresh fetch has settled, in the `finally` below, regardless of whether
  // a cache hit already painted something. See the skeleton branch and the
  // ring.noClasses branch further down for how classesReady + classes.length
  // combine to keep the skeleton appearing on every cold mount (AC3) while
  // never showing the empty state during an in-flight request (AC2).
  //
  // Judge round 1 findings on #2700:
  //   - The cache read and the fresh fetch run IN PARALLEL, not sequenced —
  //     an IndexedDB open (which can itself be slow, or hang if another tab
  //     holds an older-version connection open, see db.js's openDb) must
  //     never be able to hold up the network request that is the actual
  //     source of truth. `freshLanded` guards against the reverse race: a
  //     slow cache read resolving AFTER the fresh fetch already landed must
  //     never overwrite fresher data with stale cached data.
  //   - `classes` is cleared to `[]` up front so switching shows can never
  //     leave the PREVIOUS show's rows on screen (which would suppress the
  //     skeleton via `classes.length > 0`) until the new show's own
  //     cache/fetch lands.
  useEffect(() => {
    let cancelled = false;
    let freshLanded = false;
    setClassesReady(false);
    setClasses([]);

    if (activeShowId) {
      classesCache
        .byShow(activeShowId)
        .then((cached) => {
          if (!cancelled && !freshLanded && cached.length) setClasses(cached);
        })
        .catch(() => {
          // Local cache read failed (private mode, blocked storage, a
          // blocked/slow IndexedDB open, etc) — the fresh fetch below
          // already runs independently and is not gated on this.
        });
    }

    (async () => {
      try {
        setError(null);
        await loadClasses(activeShowId);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        freshLanded = true;
        if (!cancelled) setClassesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, loadClasses]);

  // #760 — the show's judge roster (score-judge entity, #730), for the
  // per-section (technical/quality) judge picker below. Non-fatal: an empty
  // roster just leaves the picker with no options, same treatment as tests.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeShowId) {
        setJudges([]);
        return;
      }
      try {
        const list = unwrap(await scoreApi.judges.listJudges(activeShowId));
        if (!cancelled) setJudges(list || []);
      } catch {
        if (!cancelled) setJudges([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, scoreApi]);

  // Load the show's tests to detect young-horse T/Q classes (non-fatal).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeShowId) {
        setTestsById({});
        return;
      }
      try {
        const list = unwrap(await scoreApi.tests.listTests(activeShowId));
        if (cancelled) return;
        setTestsById(Object.fromEntries((list || []).map((tst) => [tst.id, tst])));
      } catch {
        if (!cancelled) setTestsById({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, scoreApi]);

  // #2567 (class-sponsorship workstream, #2565-2570) — the org's sponsor
  // library (score-api's ENTITIES.SPONSOR, apps/score-api/src/handlers/
  // scoreboard.js's listSponsors, #1635), for the class-detail drawer's
  // sponsor picker. Org-scoped like Sponsors.jsx, not show-scoped like
  // judges/tests above — reload on org change, non-fatal like judges/tests
  // (an empty/failed load just leaves the picker with no options).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeOrgId) {
        setSponsors([]);
        return;
      }
      try {
        const items = unwrap(await scoreApi.scoreboard.listSponsors());
        if (!cancelled) setSponsors(items || []);
      } catch {
        if (!cancelled) setSponsors([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId, scoreApi]);

  // #340/#862/#3496 -- the "needs placings" / "can't place yet" facts (header
  // chips, row chips, drawer) come from BadgeCountsContext, the app shell's one
  // GET /show/:showId/badge-counts, so this page and the nav rail can never
  // disagree and the page does no counting of its own.
  const {
    needsPlacingsCount,
    cantPlaceYetCount,
    needsPlacingsClassIds: classesNeedingPlacings,
    cantPlaceYetClassIds,
    cantPlaceYetRidesByClassId,
    loadedTick: badgeLoadedTick,
  } = useBadgeCounts();
  // #3651 -- re-read with every badge-counts load: the same cadence.
  const { byClassId: storedPlaceProblems, loaded: placeProblemsLoaded } = usePlaceProblems(
    activeShowId,
    badgeLoadedTick,
  );
  // #3670 -- one answer for the header "can't place yet" chip and the Placing
  // problems filter: the stored list UNION every class badge-counts holds on
  // unfinished rides. A class the stored list lacks (never evaluated since it
  // went stuck, or the read failed) gets a row built from the rides badge-counts
  // names, so its reason chip still reads "Ride open: CNO ...".
  const placeProblemsByClassId = useMemo(() => {
    const merged = new Map(storedPlaceProblems);
    for (const classId of cantPlaceYetClassIds) {
      if (merged.has(classId)) continue;
      const blockingRides = (cantPlaceYetRidesByClassId.get(classId) ?? []).map((r) => ({
        rideId: r.id,
        competitorNumber: r.competitorNumber ?? null,
      }));
      merged.set(classId, { classId, reason: "not-final", blockingRides, manualRides: [], tiedRides: [] });
    }
    return merged;
  }, [storedPlaceProblems, cantPlaceYetClassIds, cantPlaceYetRidesByClassId]);

  // #2261 -- the show's rides, grouped by class, still feed the test-disagreement
  // tooltip, the placings-at-risk warnings and the drawer's stuck-ride list
  // (not a badge computation). Its refetch is paired with every save's class
  // reload (reloadClassesAndPlacings) so ride-derived state can't go stale.
  const {
    ridesByClassId,
    loading: ridesLoading,
    refetch: refetchPlacings,
  } = useClassesNeedingPlacings(activeShowId);

  // #2261 — every post-save reload also refreshes the shared needs-placings /
  // show-rides fetch, so the header chip, the row badges AND the
  // test-disagreement tooltip all see the server's current state after any
  // change (a re-reconcile that landed while the operator sat on /classes is
  // picked up by the next save-triggered reload, not cached forever). The
  // cold-mount effect deliberately calls plain loadClasses: the hook fetches
  // on mount by itself, calling refetch there too would double the requests.
  const reloadClassesAndPlacings = useCallback(
    async (id) => {
      await loadClasses(id);
      refetchPlacings();
    },
    [loadClasses, refetchPlacings],
  );

  const isYoungHorseClass = useCallback(
    (c) => Boolean(c.testId) && testsById[c.testId]?.scoringModel === "young-horse-average",
    [testsById],
  );

  // #2252/#2261 — the ride-side test slugs the TestDisagreementChip tooltip
  // names, derived from the show-scoped ridesByClassId the needs-placings
  // hook ALREADY fetched — zero per-class GET /ride?classId= fan-out no
  // matter how many classes carry #2251's testDisagreement flag (the old
  // useTestDisagreementRideTests hook fired one request per flagged class
  // and cached it for the life of the mount). A flagged class with no entry
  // in ridesByClassId yet (hook still loading) stays absent from the map,
  // which is the chip's "loading rides" state.
  const testDisagreementRideTestIds = useMemo(() => {
    const byId = {};
    for (const c of classes) {
      if (!c.testDisagreement?.flagged) continue;
      const rides = ridesByClassId[c.id];
      if (rides) byId[c.id] = distinctRealRideTestIds(rides);
    }
    return byId;
  }, [classes, ridesByClassId]);

  const allJudges = useMemo(() => {
    const set = new Set();
    for (const c of classes) {
      for (const names of Object.values(c.judgeAssignments || {})) {
        for (const name of names || []) if (name) set.add(name);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [classes]);

  // #865 — a roster lookup miss (stale/removed assignment) is a data
  // problem to surface quietly, not a raw userId to print in place of a
  // name. Callers that want the raw id for support can still get it —
  // ClassDetailDrawer's stale-value MenuItems put it in a tooltip.
  const scribeLabel = useCallback(
    (userId) => {
      const m = scribes.find((s) => s.userId === userId);
      return m ? userDisplayName(m) || t("common.unknownUser") : t("common.unknownUser");
    },
    [scribes, t],
  );

  // #760 — label a score-judge roster id for the section-judge Selects below.
  // #865 — same quiet-placeholder treatment as scribeLabel above.
  const judgeLabel = useCallback(
    (judgeId) => {
      const j = judges.find((jj) => jj.id === judgeId);
      if (!j) return t("common.unknownJudge");
      return userDisplayName(j) || t("common.unknownJudge");
    },
    [judges, t],
  );

  // Assign (or clear) the scribe for a SINGLE class at a position (#530), reload.
  const assign = async (classId, position, userId) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassScribeAssignment(classId, {
        position,
        userId: userId || null,
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #2421 — bulk CLASS CONFIG over the current selection: scribe positions
  // plus every per-class option class-config.js exposes a setter for.
  // `payload` is `{ dirty, values }` straight from the dialog — dirty[key]
  // is the ONLY thing that decides whether a field gets written at all (the
  // card's "write rule": untouched never re-writes, touched-back-to-its-
  // original-value still writes). Flattened into one list of per-class,
  // per-field writes so each failure is attributed to the exact class +
  // field that failed (AC10) rather than the whole class or the whole
  // submit. #2854: the whole list now goes to the server in ONE request
  // (POST /class/bulk-config, scoreApi.classes.bulkUpdateClassConfig)
  // instead of one PUT per class per field; the server reads and writes each
  // class once and reports any class+field it could not apply, which maps
  // back onto this list's labels below. Each write carries the server field
  // key it lands under (`serverField`) and an `apply` that folds its value
  // into that class's `changes` object.
  const bulkSaveClassConfig = async ({ dirty, values }) => {
    const targets = classes.filter((c) => selectedIds.has(c.id));
    const writes = [];
    // #2519: every place below that deliberately drops a dirty field for a
    // class (instead of pushing a write) records why here, so the caller
    // can tell "nothing to do" from "something was withheld" and report the
    // latter instead of a clean success.
    const skips = [];
    for (const c of targets) {
      const label = classNumberLabel(c);
      const relevantPositions = new Set(positionsForClass(c));
      for (const position of relevantPositions) {
        const key = `scribe:${position}`;
        if (!dirty[key]) continue;
        const raw = values[key];
        const userId = raw === "" || raw == null ? null : raw;
        writes.push({
          classId: c.id,
          label,
          field: `${position} scribe`,
          serverField: "scribeAssignments",
          apply: (changes) => {
            changes.scribeAssignments = { ...changes.scribeAssignments, [position]: userId };
          },
        });
      }
      // #2421 repair — BulkNumberField now lets an empty field commit
      // `null` (bulk-unset), but setClassFaultTimeLimit's own bodySchema
      // (apps/score-api/src/handlers/class-config.js) requires
      // faultTimeLimitSeconds as a positive integer with no null/unset
      // representation at all: sending null would 400 every selected class,
      // the exact defect-2 shape. Skip the write when cleared rather than
      // fire a write the API can never accept.
      if (dirty.faultTimeLimitSeconds && values.faultTimeLimitSeconds != null) {
        writes.push({
          classId: c.id,
          label,
          field: "fault time limit",
          serverField: "faultTimeLimitSeconds",
          apply: (changes) => {
            changes.faultTimeLimitSeconds = values.faultTimeLimitSeconds;
          },
        });
      } else if (dirty.faultTimeLimitSeconds) {
        // #2519: the dirty field was cleared (bulk-unset), and the skip
        // above dropped it with no trace. Record it so the operator learns
        // the fault time limit was NOT cleared, instead of the dialog
        // freezing open with nothing to say (AC1) or, worse, closing as a
        // reported success.
        skips.push({
          classId: c.id,
          label,
          field: "fault time limit",
          reason: t("ring.bulkAssign.skippedReasonBulkClearUnsupported"),
        });
      }
      if (dirty.errorDeductionScheme) {
        writes.push({
          classId: c.id,
          label,
          field: "error deduction scheme",
          serverField: "errorDeductionScheme",
          apply: (changes) => {
            changes.errorDeductionScheme = values.errorDeductionScheme;
          },
        });
      }
      if (dirty.skipUsefUpload) {
        writes.push({
          classId: c.id,
          label,
          field: "skip USEF upload",
          serverField: "skipUsefUpload",
          apply: (changes) => {
            changes.skipUsefUpload = values.skipUsefUpload === true;
          },
        });
      }
      if (dirty.autoPlace) {
        writes.push({
          classId: c.id,
          label,
          field: "auto-place",
          serverField: "autoPlace",
          apply: (changes) => {
            changes.autoPlace = values.autoPlace === true;
          },
        });
      }
      if (dirty.placedByDivision) {
        writes.push({
          classId: c.id,
          label,
          field: "placed by division",
          serverField: "placedByDivision",
          apply: (changes) => {
            changes.placedByDivision = values.placedByDivision === true;
          },
        });
      }
      if (dirty.placesAwarded) {
        // #3415 -- null (cleared) is accepted by the API and clears the
        // setting. A typed 0 can never be accepted, so it is withheld and
        // reported rather than sent to 400 every selected class.
        const v = values.placesAwarded;
        if (v == null || (Number.isInteger(v) && v >= 1)) {
          writes.push({
            classId: c.id,
            label,
            field: "places awarded",
            serverField: "placesAwarded",
            apply: (changes) => {
              changes.placesAwarded = v ?? null;
            },
          });
        } else {
          skips.push({
            classId: c.id,
            label,
            field: "places awarded",
            reason: t("ring.bulkAssign.skippedReasonPlacesAwardedInvalid"),
          });
        }
      }
      if (dirty.dshbKindOverride) {
        // #2421 repair (defect 2): the dialog's "Auto" option commits the
        // sentinel DSHB_KIND_OVERRIDE_AUTO, not a real
        // DSHB_CLASS_KINDS value — map it to null (the drawer's own
        // "clear the override" write) here, same as the falsy-clear branch
        // right below it, or dshbKindOverrideBodySchema's enum 400s every
        // selected class.
        const dshbKindOverride =
          values.dshbKindOverride && values.dshbKindOverride !== DSHB_KIND_OVERRIDE_AUTO
            ? values.dshbKindOverride
            : null;
        writes.push({
          classId: c.id,
          label,
          field: "DSHB kind override",
          serverField: "dshbKindOverride",
          apply: (changes) => {
            changes.dshbKindOverride = dshbKindOverride;
          },
        });
      }
      if (c.sectionCode === EQUITATION_SECTION_CODE && dirty.walkTrotOnly) {
        writes.push({
          classId: c.id,
          label,
          field: "walk/trot only",
          serverField: "walkTrotOnly",
          apply: (changes) => {
            changes.walkTrotOnly = values.walkTrotOnly === true;
          },
        });
      }
      if (dirty.paperEntry) {
        writes.push({
          classId: c.id,
          label,
          field: "paper scoring",
          serverField: "paperEntry",
          apply: (changes) => {
            changes.paperEntry = values.paperEntry === true;
          },
        });
      }
      // #542/#760 — qualityJudging and sectionJudgeAssignments are each
      // written as ONE object per the existing single-class setters
      // (saveQualityJudging/saveSectionJudgeAssignments above), so a
      // touched sub-field (e.g. topology) must carry the untouched
      // sub-fields forward from THIS class's own current value — never
      // from another selected class's value and never from the dialog's
      // shared "agree" display, exactly like the single-class drawer's own
      // onChange handlers already do.
      if (isYoungHorseClass(c)) {
        const qjDirty =
          dirty["qualityJudging.topology"] ||
          dirty["qualityJudging.qualityPosition"] ||
          dirty["qualityJudging.qualityJudge"];
        if (qjDirty) {
          // #2421 repair (defect 3): topology is bodySchema's one required
          // field (score-api/src/handlers/class-config.js), and
          // qualityPosition/qualityJudge only ever mean anything under
          // "two-judge" (mirrors ClassDetailDrawer, which renders those two
          // controls only when topology === "two-judge"). This dialog offers
          // them unconditionally across a mixed selection, so the resolved
          // topology for THIS class can come out undefined (no
          // qualityJudging yet, topology itself untouched -> Fastify 400,
          // required property missing) or "technical-only" (API's own
          // normalizeQualityJudging silently nulls position/judge -> a
          // reported success that changed nothing). Only send the write when
          // the resolved topology is actually "two-judge", or the operator
          // touched topology itself (a legitimate write, incl. clearing
          // position/judge by moving to technical-only) -- otherwise skip
          // this class for this field entirely rather than 400 or fake a
          // success.
          const topology = dirty["qualityJudging.topology"]
            ? values["qualityJudging.topology"]
            : c.qualityJudging?.topology;
          if (topology === "two-judge" || dirty["qualityJudging.topology"]) {
            writes.push({
              classId: c.id,
              label,
              field: "T/Q judging",
              serverField: "qualityJudging",
              apply: (changes) => {
                changes.qualityJudging = {
                  topology,
                  qualityPosition: dirty["qualityJudging.qualityPosition"]
                    ? values["qualityJudging.qualityPosition"] || null
                    : (c.qualityJudging?.qualityPosition ?? null),
                  qualityJudge: dirty["qualityJudging.qualityJudge"]
                    ? values["qualityJudging.qualityJudge"] || null
                    : (c.qualityJudging?.qualityJudge ?? null),
                  // #3372: a bulk edit of the position/judge keeps the class's
                  // declared seat counts.
                  ...(c.qualityJudging?.technicalSeats ? { technicalSeats: c.qualityJudging.technicalSeats } : {}),
                  ...(c.qualityJudging?.qualitySeats ? { qualitySeats: c.qualityJudging.qualitySeats } : {}),
                };
              },
            });
          } else {
            // #2519: the topology gate above correctly refuses to build a
            // write that would 400 or be silently discarded server-side,
            // but that refusal used to leave no trace: an operator who only
            // touched the T/Q judge on a non-two-judge class believed a
            // quality judge was assigned when it was not (the exact harm
            // this card exists to close). Record the skip.
            skips.push({
              classId: c.id,
              label,
              field: "T/Q judging",
              reason: t("ring.bulkAssign.skippedReasonNotTwoJudge"),
            });
          }
        }
        const sectionDirty = dirty["sectionJudgeAssignments.technical"] || dirty["sectionJudgeAssignments.quality"];
        if (sectionDirty) {
          writes.push({
            classId: c.id,
            label,
            field: "section judges",
            serverField: "sectionJudgeAssignments",
            apply: (changes) => {
              // #3372: the bulk pickers set the FIRST seat of a section; any further
              // seats a class already has are carried through, never dropped.
              const next = {};
              for (const section of ["technical", "quality"]) {
                const ids = sectionJudgeIds(c.sectionJudgeAssignments, section);
                const edited = dirty[`sectionJudgeAssignments.${section}`];
                const first = edited ? values[`sectionJudgeAssignments.${section}`] || null : (ids[0] ?? null);
                next[section] = first;
                if (ids.length > 1) {
                  next[`${section}Judges`] = [first, ...ids.slice(1)].filter(Boolean);
                }
              }
              changes.sectionJudgeAssignments = next;
            },
          });
        }
      }
    }

    // #2519: the old bare `return` here fired on ANY empty-writes submit,
    // including "the only dirty field was one the guards above just
    // dropped" (AC1's fault-time-limit-only repro): saving never got set,
    // there was no message, and the dialog just sat there. Skips now carry
    // their own report, so only bail silently when there is truly nothing
    // to say: no write attempted AND nothing skipped.
    if (writes.length === 0 && skips.length === 0) return;

    setSaving(true);
    setError(null);
    setBulkFailures([]);
    setBulkSkips(skips);
    setBulkWritesTotal(writes.length);
    setBulkOutcomes(null);
    const failures = [];
    const outcomes = [];
    if (writes.length > 0) {
      // ONE request for every class and field (#2854). Group the flat write
      // list back into one `changes` object per class.
      const changesByClass = new Map();
      for (const w of writes) {
        if (!changesByClass.has(w.classId)) changesByClass.set(w.classId, {});
        w.apply(changesByClass.get(w.classId));
      }
      const updates = [...changesByClass].map(([classId, changes]) => ({ classId, changes }));
      // The endpoint takes at most BULK_CLASS_CONFIG_MAX_CLASSES classes per
      // request (a select-all over a bigger show would 400 every class), so
      // send chunks one after another and merge the failures. A chunk whose
      // request fails outright reports only ITS classes' writes as failed.
      for (let i = 0; i < updates.length; i += BULK_CLASS_CONFIG_MAX_CLASSES) {
        const chunk = updates.slice(i, i + BULK_CLASS_CONFIG_MAX_CLASSES);
        const chunkIds = new Set(chunk.map((u) => u.classId));
        const chunkWrites = writes.filter((w) => chunkIds.has(w.classId));
        try {
          // Sequential by design (one request in flight at a time).
          const res = await scoreApi.classes.bulkUpdateClassConfig({ updates: chunk });
          // The server names what it could not apply as class + server field
          // (field null = the whole class: not found, or its write never
          // landed). Map each back onto the flat writes so the report names
          // the same class/field labels as before.
          const reported = unwrap(res)?.failures || [];
          // #3405: per-class result of the Placed by division re-place.
          for (const u of unwrap(res)?.updated || []) {
            if (u.placingsOutcome == null) continue;
            const target = targets.find((c) => c.id === u.classId);
            outcomes.push({ classId: u.classId, label: classNumberLabel(target), outcome: u.placingsOutcome });
          }
          for (const w of chunkWrites) {
            const hit = reported.find(
              (f) => f.classId === w.classId && (f.field == null || f.field === w.serverField)
            );
            if (hit) failures.push({ classId: w.classId, label: w.label, field: w.field, error: new Error(hit.code) });
          }
        } catch (err) {
          // The request itself failed (network, 4xx/5xx): nothing in this
          // chunk is known to have been applied, so every one of its writes
          // is reported as failed rather than leaving the dialog unknown.
          for (const w of chunkWrites) failures.push({ classId: w.classId, label: w.label, field: w.field, error: err });
        }
      }
    }
    if (writes.length > 0) {
      await reloadClassesAndPlacings(activeShowId);
    }
    setSaving(false);
    if (outcomes.length > 0) setBulkOutcomes(outcomes);
    if (failures.length === 0 && skips.length === 0) {
      // A save that re-placed classes keeps the dialog open on the per-class
      // result (its Done button closes it and clears the selection).
      if (outcomes.length === 0) {
        setSelectedIds(new Set());
        setBulkDialogOpen(false);
      }
    } else {
      // Partial failure and/or skipped class-field pairs: name what failed
      // or was withheld, keep the dialog open, and do NOT clear the
      // selection: never report a clean success over a partial write
      // (card's "Partial failure" section, AC10) or a silently skipped one
      // (#2519, AC2/AC3).
      setBulkFailures(failures);
    }
  };

  // #542 — persist a class's Technical/Quality judging topology, reload.
  const saveQualityJudging = async (classId, patch) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassQualityJudging(classId, {
        topology: patch.topology,
        qualityPosition: patch.qualityPosition ?? null,
        qualityJudge: patch.qualityJudge ?? null,
        ...(patch.technicalSeats ? { technicalSeats: patch.technicalSeats } : {}),
        ...(patch.qualitySeats ? { qualitySeats: patch.qualitySeats } : {}),
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #760 — persist a class's per-section (technical/quality) judge
  // assignment, reload. Same save/reload shape as saveQualityJudging above;
  // values are score-judge roster ids (#759's API), not bare judge names.
  const saveSectionJudgeAssignments = async (classId, patch) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassSectionJudgeAssignments(classId, {
        technical: patch.technical ?? null,
        quality: patch.quality ?? null,
        // #3372: N-seat sections carry their full ordered list.
        ...(Array.isArray(patch.technicalJudges) ? { technicalJudges: patch.technicalJudges } : {}),
        ...(Array.isArray(patch.qualityJudges) ? { qualityJudges: patch.qualityJudges } : {}),
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #570 — persist a class's fault time limit (seconds), reload. Same
  // save/reload shape as saveQualityJudging above.
  const saveFaultTimeLimit = async (classId, faultTimeLimitSeconds) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassFaultTimeLimit(classId, {
        faultTimeLimitSeconds,
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #772 (workstream error-deductions, #771-774) — persist a class's
  // error-of-course/test deduction scheme, reload. Same save/reload shape as
  // saveFaultTimeLimit above.
  const saveErrorDeductionScheme = async (classId, errorDeductionScheme) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassErrorDeductionScheme(classId, {
        errorDeductionScheme,
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #1783: persist a class's walk/trot-only flag, reload. Same save/reload
  // shape as saveErrorDeductionScheme above.
  const saveWalkTrotOnly = async (classId, walkTrotOnly) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassWalkTrotOnly(classId, {
        walkTrotOnly,
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #1117: persist a class's hidden flag (true hides it from the class-list
  // surfaces, false shows it again), reload. Same save/reload shape as
  // saveWalkTrotOnly above.
  const saveHidden = async (classId, hidden) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassHidden(classId, { hidden });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #3564: persist staff's explicit "this class is a RO/ROO split section"
  // flag (the only signal that lets an emptied class auto-hide; nothing is
  // inferred from the class name, number or sngClassID decimal), reload.
  const saveSplitSection = async (classId, splitSection) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassSplitSection(classId, { splitSection });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #3405: which classes already have results with the federation, read from
  // the same report-api show state PublishResults uses (UPLOADED / SUBMITTED /
  // CORRECTED). Only called when a Placed by division confirm is open.
  const loadPublishedStatuses = useCallback(async () => {
    const res = unwrapReport(await reportApi.federation.getFederationShowState(activeShowId));
    const byClassId = {};
    for (const row of res?.classes || []) {
      if (isPublishedFederationStatus(row.status)) byClassId[row.classId] = row.status;
    }
    return byClassId;
  }, [reportApi, activeShowId]);

  // #3405: send one class's Placed by division and remember what the server did
  // to its placings (3404's placingsOutcome) for the drawer to show.
  const sendPlacedByDivision = async (classId, placedByDivision) => {
    try {
      setSaving(true);
      setError(null);
      const res = await scoreApi.classes.setClassPlacedByDivision(classId, { placedByDivision });
      setPlacedByDivisionOutcomes((prev) => ({ ...prev, [classId]: unwrap(res)?.placingsOutcome ?? "none" }));
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // The drawer switch. A class that already has placings gets the re-place
  // confirm first; any other class is sent straight away.
  const savePlacedByDivision = (classId, placedByDivision) => {
    setPlacedByDivisionOutcomes((prev) => ({ ...prev, [classId]: null }));
    const target = classes.find((c) => c.id === classId);
    const atRisk = target ? classesWithPlacingsAtRisk([target], ridesByClassId, placedByDivision) : [];
    if (atRisk.length > 0) {
      setPlacedByDivisionConfirm({ classId, placedByDivision, classes: atRisk });
      return;
    }
    return sendPlacedByDivision(classId, placedByDivision);
  };

  // #2415: persist a class's paper-entry flag (paper-scoring workstream),
  // reload. Same save/reload shape as saveWalkTrotOnly above.
  const savePaperEntry = async (classId, paperEntry) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassPaperEntry(classId, {
        paperEntry,
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #3655: persist a class's "auto-place this class" switch, reload. Same
  // save/reload shape as savePaperEntry above.
  const saveAutoPlace = async (classId, autoPlace) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassAutoPlace(classId, { autoPlace });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #2277: persist a class's DSHB kind override (null clears it back to the
  // className/fvdTestId guess), reload. Same save/reload shape as
  // saveWalkTrotOnly above.
  const saveDshbKindOverride = async (classId, dshbKindOverride) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassDshbKindOverride(classId, {
        dshbKindOverride,
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #2571: persist a class's optional friendly display name (shown on the
  // scoreboard instead of the FVD className); blank/null clears it. Same
  // save/reload shape as saveDshbKindOverride above.
  const saveFriendlyName = async (classId, friendlyName) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassFriendlyName(classId, { friendlyName });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #3294: persist a class's number of places awarded (default 8). Same
  // save/reload shape as saveFriendlyName above; the drawer only sends a whole
  // number of 1 or more.
  const sendPlacesAwarded = async (classId, placesAwarded) => {
    try {
      setSaving(true);
      setError(null);
      const res = await scoreApi.classes.setClassPlacesAwarded(classId, { placesAwarded });
      setPlacesAwardedOutcomes((prev) => ({ ...prev, [classId]: unwrap(res)?.placingsOutcome ?? "none" }));
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #3415: the drawer field (a whole number, or null to clear). A class that
  // already has placings gets the same re-place confirm as Placed by division
  // (#3405) first; any other class is sent straight away.
  const savePlacesAwarded = (classId, placesAwarded) => {
    setPlacesAwardedOutcomes((prev) => ({ ...prev, [classId]: null }));
    const target = classes.find((c) => c.id === classId);
    const atRisk = target ? classesWithPlacingsAtRiskForPlaces([target], ridesByClassId, placesAwarded) : [];
    if (atRisk.length > 0) {
      setPlacesAwardedConfirm({ classId, placesAwarded, classes: atRisk });
      return;
    }
    return sendPlacesAwarded(classId, placesAwarded);
  };

  // #3353: bind a real catalog test to a class whose FVD test is unmatched
  // (testId null clears the binding). Unlike the saves above this does NOT
  // swallow the error into the page banner: the drawer's AssignTestPanel maps
  // score-api's 409 codes (test_already_matched) to a specific message, so the
  // error is rethrown to it.
  const saveClassTest = async (classId, testId, reason) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassTest(classId, reason ? { testId, reason } : { testId });
      await reloadClassesAndPlacings(activeShowId);
    } finally {
      setSaving(false);
    }
  };

  // #3351: persist (or clear, count null) a class's expected judge count
  // override. Same save/reload shape as savePlacesAwarded above.
  const saveExpectedJudgeCount = async (classId, { count, reason }) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassExpectedJudgeCount(classId, { count, reason });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #2567: persist a class's sponsor + prize together. score-api's
  // setClassSponsorship (class-config.js) is a FULL REPLACE of both fields
  // (`const { sponsorId = null, prize } = request.body`) — sending only one
  // silently clears the other (recorded on card #2565). The drawer always
  // hands both current values here, never just the one the operator
  // touched; see ClassDetailDrawer's commitSponsorship. Same save/reload
  // shape as saveWalkTrotOnly above.
  const saveSponsorship = async (classId, { sponsorId, prize }) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassSponsorship(classId, {
        sponsorId,
        prize,
      });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #3611: persist one or more of a class's sponsor / prize / sponsor-logo
  // switches (a key left out is unchanged on the server).
  const saveSponsorPrizeDisplay = async (classId, switches) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassSponsorPrizeDisplay(classId, switches);
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #1627: persist a class's arena / averageRideTime override. One block,
  // FULL REPLACE on the server (an omitted key clears), so the drawer always
  // sends all of it; see ClassDetailDrawer's commitTestOverrides.
  const saveTestOverrides = async (classId, { arena, averageRideTime }) => {
    try {
      setSaving(true);
      setError(null);
      await scoreApi.classes.setClassTestOverrides(classId, { arena, averageRideTime });
      await reloadClassesAndPlacings(activeShowId);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  // #3681 -- classes with riders and no scribe, over the classes the table can
  // show at all (#591), so the badge count and the filtered list always match.
  const noScribeWithRidersIds = useMemo(
    () =>
      new Set(
        classesWithRidersAndNoScribe(
          classes.filter((c) => classProblem(c) === null),
          ridesByClassId,
        ).map((c) => c.id),
      ),
    [classes, ridesByClassId],
  );

  // #553 — filter → group → sort. Groups by day / ring / day+ring (or none),
  // sorted within each group by class number or name.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return classes.filter((c) => {
      // #591 — never show classes with no ring or no scheduled start time,
      // unless the URL asks for exactly one of those problems (#3662). The
      // "scheduled" test is classDayKey (scheduledTime, else time), the same
      // day the Settings Rings and days counts use.
      if (classProblem(c) !== (problemParam || null)) return false;
      if (ringParam && normalizeRing(c.ring) !== ringParam) return false;
      if (dayParam && classDayKey(c) !== dayParam) return false;
      // #575/#593 — only classes that actually need a scribe (judged positions
      // with at least one unassigned) so the toggle visibly filters.
      if (noScribeOnly && !isMissingScribe(c)) return false;
      // #340 — only classes scored but not yet placed.
      if (needsPlacingsOnly && !classesNeedingPlacings.has(c.id)) return false;
      if (cantPlaceOnly && !placeProblemsByClassId.has(c.id)) return false;
      if (noScribeWithRidersOnly && !noScribeWithRidersIds.has(c.id)) return false;
      if (!q) return true;
      return `${classNumberLabel(c)} ${c.className ?? ""}`.toLowerCase().includes(q);
    });
  }, [classes, search, noScribeOnly, needsPlacingsOnly, classesNeedingPlacings, cantPlaceOnly, placeProblemsByClassId, noScribeWithRidersOnly, noScribeWithRidersIds, ringParam, dayParam, problemParam]);

  // #3627 -- the Filter menu's options, per-filter match counts and the "Y" in
  // "X of Y classes". Y/counts are over the classes the table can ever show
  // (#591: ring + scheduled start), BEFORE any filter or search, so a count
  // answers "how many would this filter leave me with".
  const classFilterOptions = useMemo(
    () => CLASS_FILTERS.map((f) => ({ key: f.key, label: t(f.labelKey) })),
    [t],
  );
  const scheduledClasses = useMemo(
    () => classes.filter((c) => classProblem(c) === (problemParam || null)),
    [classes, problemParam],
  );
  const classFilterCounts = useMemo(
    () => ({
      noScribe: scheduledClasses.filter((c) => isMissingScribe(c)).length,
      needsPlacings: scheduledClasses.filter((c) => classesNeedingPlacings.has(c.id)).length,
      // #3670 -- null until place-problems has answered OK once: no number
      // beats a 0 we could not read.
      cantPlace: placeProblemsLoaded
        ? scheduledClasses.filter((c) => placeProblemsByClassId.has(c.id)).length
        : null,
      noScribeWithRiders: scheduledClasses.filter((c) => noScribeWithRidersIds.has(c.id)).length,
    }),
    [scheduledClasses, classesNeedingPlacings, placeProblemsByClassId, placeProblemsLoaded, noScribeWithRidersIds],
  );

  const groups = useMemo(() => {
    const keyOf = (c) => {
      if (groupBy === "day") return classDay(c) || "";
      if (groupBy === "ring") return normalizeRing(c.ring);
      if (groupBy === "day-ring") return [classDay(c), normalizeRing(c.ring)].filter(Boolean).join(" · ");
      return "";
    };
    const map = new Map();
    for (const c of filtered) {
      const k = keyOf(c);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    }
    // #572 — rows with no value for the active sort field sort to the END
    // (nulls last). compareByClassNumber already trails no-number classes;
    // the name comparator pushes blank names last instead of first.
    const byNameNullsLast = (a, b) => {
      const an = (a.className || "").trim();
      const bn = (b.className || "").trim();
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn);
    };
    // #573 — sort by scheduled start time, nulls (unscheduled) last.
    const byStartTimeNullsLast = (a, b) => {
      const at = (a.scheduledTime || "").trim();
      const bt = (b.scheduledTime || "").trim();
      if (!at && !bt) return 0;
      if (!at) return 1;
      if (!bt) return -1;
      return at.localeCompare(bt, undefined, { numeric: true });
    };
    const sortRows = (rows) => {
      if (sortBy === "name") return [...rows].sort(byNameNullsLast);
      if (sortBy === "time") return [...rows].sort(byStartTimeNullsLast);
      return sortByClassNumber(rows);
    };
    // #653 — day groups must sort chronologically, not alphabetically on the
    // rendered weekday label (classDay() = "Thu"/"Fri"/"Sat"; alphabetically
    // that's Fri < Sat < Thu, exactly backwards). Sort on classDayKey(), the
    // sortable YYYY-MM-DD slice of scheduledTime (#634) — never on the label
    // text. Every row in a "day"/"day-ring" group shares one scheduledTime day
    // (that's what grouped them), so the first row's key represents the group.
    // "day-ring" ties within the same day on the full key ("Thu · A" vs
    // "Thu · B"), which — since the day prefix matches — falls through to
    // comparing the ring suffix. Plain "ring" grouping is untouched: it still
    // sorts alphabetically on the ring label.
    const groupDayKey = (rows) => classDayKey(rows[0]) || "";
    const compareGroups = ([keyA, rowsA], [keyB, rowsB]) => {
      if (groupBy === "day" || groupBy === "day-ring") {
        const dayCmp = groupDayKey(rowsA).localeCompare(groupDayKey(rowsB));
        if (dayCmp !== 0) return dayCmp;
      }
      return String(keyA).localeCompare(String(keyB), undefined, { numeric: true });
    };
    return [...map.entries()]
      .sort(compareGroups)
      .map(([k, rows]) => [k, sortRows(rows)]);
  }, [filtered, groupBy, sortBy]);

  const groupHeader = (key) => {
    if (groupBy === "none") return null;
    if (groupBy === "ring") return key ? t("ring.ringLabel", { ring: key }) : t("ring.unassignedRing");
    if (groupBy === "day") return key || t("ring.noDay");
    return key || t("ring.unassignedRing"); // day-ring
  };

  const selectedClass = classes.find((c) => c.id === selectedClassId) ?? null;

  // #1055 — bulk-select derived state. `filtered` (not `groups`) is the
  // right "currently visible" set: groups only reshapes/buckets filtered
  // rows for display, a collapsed group's rows are still on-screen-relevant
  // for "select all visible" (they're one click away, not filtered out).
  const visibleIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const visibleSelectedCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleSelectAllVisible = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const toggleSelectOne = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectedClasses = useMemo(
    () => classes.filter((c) => selectedIds.has(c.id)),
    [classes, selectedIds],
  );

  // Union of judged positions across every selected class, in JUDGE_POSITIONS
  // order, for the bulk dialog's per-position pickers.
  const bulkPositions = useMemo(() => {
    const set = new Set();
    for (const c of selectedClasses) {
      for (const p of positionsForClass(c)) set.add(p);
    }
    return JUDGE_POSITIONS.filter((p) => set.has(p));
  }, [selectedClasses]);

  // #867 — tab title: once the URL points at a specific class (:classId,
  // #863's deep-linkable detail drawer), reflect that class's number/name so
  // an operator with several class tabs open can tell them apart; the plain
  // list title otherwise (including while `classes` is still loading, before
  // `selectedClass` can resolve).
  useDocumentTitle(
    selectedClassId && selectedClass
      ? t("title.class", { label: classNumberLabel(selectedClass) })
      : t("title.classes"),
  );

  // #2700 — the full-page skeleton is needed only when there is truly
  // nothing to paint yet. Judge round 1 finding 5: the org-members load
  // (`loading`, from useOrgMembers/scribesLoading) has nothing to do with
  // WHICH classes render, only with the scribe column inside each row (which
  // already degrades to "Missing" for an unresolved assignment, same as a
  // genuinely empty roster) — gating the classes table on it meant a cache
  // hit could not paint until org-members also settled, contradicting AC1.
  // It is deliberately dropped here. `loadingShow` (from useActiveShow)
  // stays: it is what determines `activeShowId` itself, which this whole
  // effect is keyed on, so there is genuinely nothing this page can decide
  // to paint before it resolves (not even "no shows"). The remaining term,
  // `!classesReady && classes.length === 0`, is the classes fetch itself:
  // in flight with no cached class list having painted anything. A cache
  // hit paints `classes` synchronously in the effect above and skips this
  // branch even while classesReady is still false, satisfying "paint the
  // cached list before the fresh fetch resolves" (AC1). A cold mount with
  // nothing cached stays on this branch until classesReady flips true, so
  // the skeleton appears on EVERY cold mount (AC3), not just when
  // `loadingShow` happens to still be running.
  if (loadingShow || (!classesReady && classes.length === 0)) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom color="text.primary">
          {t("ring.classesTitle")}
        </Typography>
        <TableSkeleton rows={4} columns={2} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* #614 — match the loading-state heading + the "Classes" nav item so the
          title stays "Classes" from first paint through data load (no flip to
          the old "Ring → Scribe assignments"). */}
      {/* #3627 -- ONE toolbar row: title + needing-placings chip(s) on the left,
          search / Group by / Sort by / Filter on the right, all size small.
          Wraps on narrow screens (title on its own line, controls under it).
          #614 -- the title stays "Classes" from first paint through data load. */}
      <Stack
        direction="row"
        alignItems="center"
        useFlexGap
        sx={{ mb: 2, flexWrap: "wrap", gap: 1.5 }}
        data-testid="classes-toolbar"
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flex: { xs: "1 1 100%", md: "1 1 auto" }, minWidth: 0 }}
        >
          <Typography variant="h5" color="text.primary">
            {t("ring.classesTitle")}
          </Typography>
          {/* #340 — count of classes scored but not yet placed, visible without
              opening the filter below. */}
          {activeShowId && (
            <Chip
              size="small"
              color={needsPlacingsCount > 0 ? "warning" : "default"}
              label={t("ring.needsPlacingsCount", { count: needsPlacingsCount })}
              data-testid="needs-placings-count"
              clickable={needsPlacingsCount > 0}
              onClick={needsPlacingsCount > 0 ? () => navigate("/placings") : undefined}
              sx={
                needsPlacingsCount > 0
                  ? { cursor: "pointer", "&:hover": { filter: "brightness(0.92)" } }
                  : undefined
              }
            />
          )}
          {/* #3462 -- classes past their last ride but held open by unfinished
              rides: its own number, never folded into the one above. Shown only
              when there are some, so a show with none is unchanged. */}
          {activeShowId && cantPlaceYetCount > 0 && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={t("ring.cantPlaceYetCount", { count: cantPlaceYetCount })}
              data-testid="cant-place-yet-count"
              clickable
              onClick={() => setAppliedFilters((prev) => (prev.includes("cantPlace") ? prev : [...prev, "cantPlace"]))}
              sx={{ cursor: "pointer", "&:hover": { filter: "brightness(0.92)" } }}
            />
          )}
          {/* #3681 -- replaces the per-class #3555 banner: just the number, and
              a click shows exactly those classes (the same predicate). */}
          {activeShowId && noScribeWithRidersIds.size > 0 && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t("ring.noScribeWithRidersCount", { count: noScribeWithRidersIds.size })}
              data-testid="no-scribe-with-riders-count"
              clickable
              onClick={() =>
                setAppliedFilters((prev) => (prev.includes("noScribeWithRiders") ? prev : [...prev, "noScribeWithRiders"]))
              }
              sx={{ cursor: "pointer", "&:hover": { filter: "brightness(0.92)" } }}
            />
          )}
        </Stack>
        {activeShowId && (
          <>
            <TextField
              size="small"
              placeholder={t("ring.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: "100%", sm: 240 } }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="class-group-label">{t("ring.groupBy")}</InputLabel>
              <Select
                labelId="class-group-label"
                label={t("ring.groupBy")}
                value={groupBy}
                onChange={(e) => changeGroupBy(e.target.value)}
              >
                <MenuItem value="none">{t("ring.groupNone")}</MenuItem>
                <MenuItem value="day">{t("ring.groupDay")}</MenuItem>
                <MenuItem value="ring">{t("ring.groupRing")}</MenuItem>
                <MenuItem value="day-ring">{t("ring.groupDayRing")}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="class-sort-label">{t("ring.sortBy")}</InputLabel>
              <Select
                labelId="class-sort-label"
                label={t("ring.sortBy")}
                value={sortBy}
                onChange={(e) => changeSortBy(e.target.value)}
              >
                <MenuItem value="number">{t("ring.sortNumber")}</MenuItem>
                <MenuItem value="name">{t("ring.sortName")}</MenuItem>
                <MenuItem value="time">{t("ring.sortTime")}</MenuItem>
              </Select>
            </FormControl>
            {/* #3240/#3627 -- checkbox Filter menu (replaces the #575 "Missing
                scribe" and #340 "Needs placings" switches). Same filter rules;
                combines with search + grouping + sort. */}
            <AddFilterMenu
              variant="checkbox"
              testIdPrefix="class-filter"
              filters={classFilterOptions}
              counts={classFilterCounts}
              applied={appliedFilters}
              onChange={setAppliedFilters}
            />
          </>
        )}
      </Stack>

      {/* #3652 -- the ring/day the Settings table linked here with: one removable
          chip each, so the narrowing is visible and undoable. */}
      {activeShowId && (ringParam || dayParam || problemParam) && (
        <Stack
          direction="row"
          alignItems="center"
          useFlexGap
          sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
          data-testid="class-url-filter-row"
        >
          {problemParam && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={problemParam === "noRing" ? t("ring.urlFilterProblemNoRing") : t("ring.urlFilterProblemNoDay")}
              data-testid="class-url-filter-problem"
              onDelete={() => clearUrlFilter("problem")}
              sx={{ whiteSpace: "nowrap" }}
            />
          )}
          {ringParam && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t("ring.urlFilterRing", { ring: ringParam })}
              data-testid="class-url-filter-ring"
              onDelete={() => clearUrlFilter("ring")}
              sx={{ whiteSpace: "nowrap" }}
            />
          )}
          {dayParam && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={formatDayKey(dayParam)}
              data-testid="class-url-filter-day"
              onDelete={() => clearUrlFilter("day")}
              sx={{ whiteSpace: "nowrap" }}
            />
          )}
        </Stack>
      )}

      {/* #3627 -- only with 1+ filters on: a removable chip per filter, a
          Clear filters link and "X of Y classes". */}
      {activeShowId && appliedFilters.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          useFlexGap
          sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
          data-testid="class-filter-chip-row"
        >
          <Typography variant="overline" color="text.secondary">
            {t("common.filtersShowing")}
          </Typography>
          {appliedFilters.map((key) => {
            const f = classFilterOptions.find((o) => o.key === key);
            if (!f) return null;
            return (
              <Chip
                key={key}
                size="small"
                color="primary"
                variant="outlined"
                label={f.label}
                data-testid={`class-filter-chip-${key}`}
                onDelete={() => setAppliedFilters(appliedFilters.filter((k) => k !== key))}
                deleteIcon={
                  <CloseIcon
                    titleAccess={t("common.removeFilter", { filter: f.label })}
                    data-testid={`class-filter-chip-remove-${key}`}
                  />
                }
                sx={{ whiteSpace: "nowrap" }}
              />
            );
          })}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setAppliedFilters([])}
            data-testid="class-filter-clear"
          >
            {t("common.clearFilters")}
          </Link>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary" data-testid="class-filter-shown-count">
            {t("common.classesShownOfTotal", { shown: filtered.length, total: scheduledClasses.length })}
          </Typography>
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("ring.loadError")}
        </Alert>
      )}

      {!activeShowId && <Typography color="text.secondary">{t("ring.noShows")}</Typography>}

      {scribes.length === 0 && activeShowId && !membersLoading && !scribesError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("ring.noScribes")}
        </Alert>
      )}

      {/* #1055/#3627 -- selection bar: hidden until 1+ rows are picked, then
          "N selected", Configure selected and Clear (unpicks every row).
          Sits above the table so it stays visible regardless of
          grouping/collapse state. */}
      {activeShowId && selectedIds.size > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 1, px: 1.5, minHeight: 44, borderRadius: 1, bgcolor: "action.selected" }}
          data-testid="bulk-selection-bar"
        >
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} data-testid="bulk-selected-count">
            {t("ring.bulkAssign.selectedCount", { count: selectedIds.size })}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setBulkFailures([]);
              setBulkOutcomes(null);
              setBulkDialogOpen(true);
            }}
            data-testid="bulk-assign-scribes-button"
          >
            {t("ring.bulkAssign.assignButton")}
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-selection">
            {t("ring.bulkAssign.clearSelection")}
          </Button>
        </Stack>
      )}

      {/* #2700 AC2 — gated on classesReady so this can never render while the
          classes request is still in flight (e.g. a cache-painted list that
          the search/filter narrows to zero matches, mid-fetch — the
          skeleton branch above already covers the true cold-mount case). */}
      {activeShowId && classesReady && groups.length === 0 && (
        <Typography color="text.secondary">{t("ring.noClasses")}</Typography>
      )}

      {/* #672 — a real MUI Table (matching ClassPlacings.jsx's pattern) so the
          number/name/scribe/time/day/ring columns line up down the page,
          instead of a List whose Stack of chips landed wherever the class
          name happened to end. Groups render as full-width rows spanning all
          columns; collapsing a group just omits its rows from this single
          TableBody, so column widths stay computed across every group. */}
      {activeShowId && groups.length > 0 && (
        <TableContainer data-testid="classes-table">
          <Table size="small">
            <TableHead>
              <TableRow>
                {/* #1055 — select-all checkbox, top-left of the table. Toggles
                    only the currently-visible/filtered rows (`visibleIds`),
                    never rows hidden by the active search/filters. */}
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                    inputProps={{ "aria-label": t("ring.bulkAssign.selectAllVisible") }}
                    data-testid="select-all-classes"
                  />
                </TableCell>
                <TableCell>{t("ring.columns.number")}</TableCell>
                <TableCell>{t("ring.columns.name")}</TableCell>
                <TableCell>{t("ring.columns.scribe")}</TableCell>
                <TableCell align="right">{t("ring.columns.time")}</TableCell>
                <TableCell>{t("ring.columns.day")}</TableCell>
                <TableCell>{t("ring.columns.ring")}</TableCell>
                {/* #1062 — judge column added at the end so it doesn't shift
                    any of the existing positionally-indexed columns. */}
                <TableCell>{t("ring.columns.judge")}</TableCell>
                {/* #554 — FVD push-state icon column, added at the end for
                    the same reason as #1062's judge column above. */}
                <TableCell align="center">{t("ring.columns.fvdPush")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(([key, rows]) => {
                const header = groupHeader(key);
                const isCollapsed = header && collapsedGroups.has(key);
                return (
                  <Fragment key={key || "__none__"}>
                    {header && (
                      // #574 — click the header to collapse/expand this group's rows.
                      <TableRow
                        hover
                        onClick={() => toggleGroup(key)}
                        sx={{ cursor: "pointer", userSelect: "none" }}
                        data-testid={`group-header-${key || "none"}`}
                      >
                        <TableCell colSpan={9} sx={{ borderBottom: isCollapsed ? "none" : undefined }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconButton size="small" sx={{ p: 0 }} aria-label={t("ring.toggleGroup")}>
                              {isCollapsed ? (
                                <ChevronRightIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                            <Typography variant="overline" color="text.secondary">
                              {header} ({rows.length})
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                    {!isCollapsed &&
                      rows.map((c) => {
                        const summary = scribeSummary(c, scribeLabel);
                        const missing = missingScribePositions(c);
                        const judgePanel = judgeSummary(c);
                        return (
                          <TableRow
                            key={c.id}
                            hover
                            data-testid={`class-row-${c.id}`}
                            sx={{ cursor: "pointer" }}
                            onClick={() => navigate(`/classes/${c.id}`)}
                          >
                            {/* #1055 — row checkbox. stopPropagation so checking
                                a row doesn't also fire the row's own onClick
                                (which navigates into the class detail drawer). */}
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={selectedIds.has(c.id)}
                                onChange={(e) => toggleSelectOne(c.id, e.target.checked)}
                                inputProps={{
                                  "aria-label": t("ring.bulkAssign.selectRow", {
                                    label: classNumberLabel(c),
                                  }),
                                }}
                                data-testid={`select-class-${c.id}`}
                              />
                            </TableCell>
                            {/* #864 — shared ClassLink, real anchor semantics into
                                /classes/:classId (keyboard/cmd-click-able) on top of
                                the row's own onClick above. #673 keeps the number and
                                name in separate, non-duplicating columns, so each
                                passes its own text as `children` rather than the
                                component's default classNumberLabel() (which would
                                repeat the number in both cells). */}
                            <TableCell>
                              <ClassLink classId={c.id}>{c.classNumber ?? ""}</ClassLink>
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {/* #865 — a class missing its own name is a data
                                  problem to surface quietly, not the class's own
                                  id printed in place of a name. The id is still
                                  one click away (row onClick + the ClassLink
                                  href above), so no separate tooltip needed. */}
                              <ClassLink classId={c.id}>{c.className || t("common.unknownClass")}</ClassLink>
                              {/* #340 — badge on the row for a class that's scored
                                  but has no placings recorded, matching the #575
                                  no-scribe pattern. */}
                              {classesNeedingPlacings.has(c.id) && (
                                <NeedsPlacingsChip
                                  classId={c.id}
                                  data-testid={`needs-placings-badge-${c.id}`}
                                  sx={{ ml: 1 }}
                                />
                              )}
                              {/* #3462 -- done being scored, but unfinished rides
                                  hold the class from being placed. */}
                              {/* #3500 -- the count is the length of the list the
                                  server sent for this class: no list, no chip. */}
                              {/* #3651 -- under the Placing problems filter the
                                  one reason chip replaces this chip, so the same
                                  open-ride fact is never on a row twice. */}
                              {cantPlaceOnly && placeProblemsByClassId.has(c.id) && (
                                <PlaceProblemChip
                                  problem={placeProblemsByClassId.get(c.id)}
                                  onOpen={() => navigate(`/classes/${c.id}`)}
                                  data-testid={`place-problem-chip-${c.id}`}
                                  sx={{ ml: 1, verticalAlign: "middle" }}
                                />
                              )}
                              {!(cantPlaceOnly && placeProblemsByClassId.has(c.id)) &&
                                cantPlaceYetRidesByClassId.get(c.id)?.length > 0 && (
                                <CantPlaceYetChip
                                  count={cantPlaceYetRidesByClassId.get(c.id).length}
                                  data-testid={`cant-place-yet-badge-${c.id}`}
                                  sx={{ ml: 1 }}
                                />
                              )}
                              {/* #1117 — a hidden class stays listed HERE (this is
                                  where an admin unsets it), but is marked. Every
                                  other class-list surface drops it. */}
                              {c.hidden === true && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={t("ring.hiddenBadge")}
                                  data-testid={`hidden-badge-${c.id}`}
                                  sx={{ ml: 1 }}
                                />
                              )}
                              {/* #2252 — class/ride test-disagreement flag from
                                  #2251, readable right here without opening the
                                  detail drawer (see TestDisagreementChip's own
                                  doc comment for why no "fix it here" control). */}
                              <TestDisagreementChip
                                testDisagreement={c.testDisagreement}
                                classTestId={c.testId}
                                rideTestIds={testDisagreementRideTestIds[c.id]}
                                testsById={testsById}
                              />
                            </TableCell>
                            <TableCell data-testid={`scribe-cell-${c.id}`}>
                              {summary}
                              {missing.length > 0 && (
                                <>
                                  {summary ? " · " : ""}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    data-testid={`scribe-missing-${c.id}`}
                                  >
                                    {t("ring.missingPositions", { positions: missing.join(", ") })}
                                  </Typography>
                                </>
                              )}
                            </TableCell>
                            {/* #573 — scheduled start time; right-aligned so times read
                                as a clean vertical column when Sort by is Start time. */}
                            <TableCell align="right">
                              {formatStartTime(c.scheduledTime, c.scheduledTimeZone) || t("ring.noTime")}
                            </TableCell>
                            <TableCell>{classDay(c) && <Chip size="small" label={classDay(c)} />}</TableCell>
                            <TableCell>
                              {c.ring && <Chip size="small" label={t("ring.ringLabel", { ring: c.ring })} />}
                            </TableCell>
                            {/* #1062 — FVD-reconciled judge panel for the class,
                                e.g. "C: Jane Smith · H: Bob Jones". A class with
                                no judge panel yet (or none of FVD's positions
                                resolved a name) shows a muted empty state
                                rather than a blank cell. */}
                            <TableCell data-testid={`judge-cell-${c.id}`}>
                              {judgePanel || (
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {t("ring.noJudge")}
                                </Typography>
                              )}
                            </TableCell>
                            {/* #554 — FVD-push status icon: gray/green/red
                                Fox Village logo reflecting the class's
                                aggregate fvdPushState from score-api. */}
                            <TableCell align="center">
                              <FvdPushStatusIcon fvdPushState={c.fvdPushState} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ClassDetailDrawer
        open={!!selectedClass}
        onClose={() => navigate("/classes")}
        classItem={selectedClass}
        // #3277 -- same needs-placings fact as the row chip, linked to the
        // class's placing form.
        needsPlacings={selectedClass ? classesNeedingPlacings.has(selectedClass.id) : false}
        // #3462 -- the unfinished rides holding the class open, listed with links.
        cantPlaceRides={selectedClass ? (cantPlaceYetRidesByClassId.get(selectedClass.id) ?? []) : []}
        // #3651 -- the server's stored Placing problem for the class, printed
        // in full with its fix buttons (ADR 0004).
        placeProblem={selectedClass ? (placeProblemsByClassId.get(selectedClass.id) ?? null) : null}
        // #2369: the catalog test def for the drawer's resolveErrorTiers
        // call (packages/score-common/src/utils/scoring.js), same testsById
        // lookup isYoungHorseClass above already uses. undefined (not an
        // error) until testsById has loaded or if the class has no testId.
        test={selectedClass ? testsById[selectedClass.testId] : undefined}
        scribes={scribes}
        saving={saving}
        positionsForClass={positionsForClass}
        scribeLabel={scribeLabel}
        onAssign={assign}
        isYoungHorse={isYoungHorseClass}
        allJudges={allJudges}
        onSaveQualityJudging={saveQualityJudging}
        onSaveFaultTimeLimit={saveFaultTimeLimit}
        onSaveErrorDeductionScheme={saveErrorDeductionScheme}
        onSaveWalkTrotOnly={saveWalkTrotOnly}
        onSaveHidden={saveHidden}
        onSaveSplitSection={saveSplitSection}
        onSaveDshbKindOverride={saveDshbKindOverride}
        onSaveFriendlyName={saveFriendlyName}
        onSaveTest={saveClassTest}
        onSavePlacesAwarded={savePlacesAwarded}
        onSaveExpectedJudgeCount={saveExpectedJudgeCount}
        sectionStyle={showSectionStyle(activeShow)}
        onSavePaperEntry={savePaperEntry}
        onSaveAutoPlace={saveAutoPlace}
        showAutoPlaceEnabled={activeShow?.autoPlaceClasses !== false}
        onSavePlacedByDivision={savePlacedByDivision}
        placedByDivisionOutcome={selectedClass ? placedByDivisionOutcomes[selectedClass.id] ?? null : null}
        placesAwardedOutcome={selectedClass ? placesAwardedOutcomes[selectedClass.id] ?? null : null}
        ridesLoading={ridesLoading}
        sponsors={sponsors}
        onSaveSponsorship={saveSponsorship}
        sponsorPrizeMode={sponsorPrizeModeOf(activeShow)}
        onSaveSponsorPrizeDisplay={saveSponsorPrizeDisplay}
        onSaveTestOverrides={saveTestOverrides}
        judges={judges}
        judgeLabel={judgeLabel}
        onSaveSectionJudgeAssignments={saveSectionJudgeAssignments}
      />

      <BulkAssignScribesDialog
        open={bulkDialogOpen}
        onClose={() => {
          // Closing the per-class result (#3405) also ends the selection it
          // was made on, the same as a clean save without one.
          if (bulkOutcomes && bulkFailures.length === 0 && bulkSkips.length === 0) setSelectedIds(new Set());
          setBulkDialogOpen(false);
          setBulkFailures([]);
          setBulkSkips([]);
          setBulkOutcomes(null);
        }}
        classes={selectedClasses}
        positions={bulkPositions}
        scribes={scribes}
        judges={judges}
        allJudges={allJudges}
        isYoungHorse={isYoungHorseClass}
        saving={saving}
        error={error}
        partialFailure={
          bulkFailures.length > 0 ? { failures: bulkFailures, totalWrites: bulkWritesTotal } : null
        }
        skipped={bulkSkips.length > 0 ? bulkSkips : null}
        ridesByClassId={ridesByClassId}
        ridesLoading={ridesLoading}
        loadPublished={loadPublishedStatuses}
        outcomes={bulkOutcomes}
        canAssignScribes={canAssignScribes}
        onSubmit={bulkSaveClassConfig}
      />

      {placedByDivisionConfirm && (
        <PlacedByDivisionConfirmDialog
          classes={placedByDivisionConfirm.classes}
          loadPublished={loadPublishedStatuses}
          saving={saving}
          onCancel={() => setPlacedByDivisionConfirm(null)}
          onConfirm={async () => {
            const { classId, placedByDivision } = placedByDivisionConfirm;
            setPlacedByDivisionConfirm(null);
            await sendPlacedByDivision(classId, placedByDivision);
          }}
        />
      )}

      {placesAwardedConfirm && (
        <PlacedByDivisionConfirmDialog
          classes={placesAwardedConfirm.classes}
          loadPublished={loadPublishedStatuses}
          saving={saving}
          titleKey="ring.placesAwardedConfirmTitle"
          warningKey="ring.placesAwardedWarning"
          onCancel={() => setPlacesAwardedConfirm(null)}
          onConfirm={async () => {
            const { classId, placesAwarded } = placesAwardedConfirm;
            setPlacesAwardedConfirm(null);
            await sendPlacesAwarded(classId, placesAwarded);
          }}
        />
      )}
    </Box>
  );
}
